import {
  EFormFieldSource,
  ICRUDFormDiMetadata,
  ICRUDFormField,
  IFormMetadataByStep,
  IFormMetadataByStepResponse,
  IFormSection,
  IWFParameter
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { defaultElementTypeFor } from "./form-element-type-options";

/**
 * A form field for a Business Object parameter. A BO param this step maps as an **output** is
 * pre-selected and editable (the user must capture it); every other BO param is a read-only,
 * unselected reference row the user can opt into. Element type is inferred from the data type.
 */
export const buildBoParamField = (param: IWFParameter, isOutput: boolean): ICRUDFormField => ({
  id: Guid.create().toString(),
  isEnabled: isOutput,
  name: param.name,
  typeName: param.dataType,
  elementType: defaultElementTypeFor(param.dataType),
  isRequired: false,
  isReadonlyField: !isOutput,
  decimalScale: null,
  source: EFormFieldSource.WorkflowBusinessObjectParameter,
  linkedWorkflowBoParameterName: param.name,
  linkedWorkflowBoParameterId: param.id
});

/**
 * Guarantees the "BO params are always on the form" invariant. Every current Business Object
 * *scalar* parameter must have a field — one added to the process after this form was generated
 * (or one the backend never seeded) is appended to the first section as a reference row: selected
 * + editable when this step maps it as an output, otherwise unselected + read-only. Object-typed
 * BO params can't be a single form field, so they're skipped.
 *
 * Only *adds* missing fields — existing fields keep whatever selection the user (or the draft
 * normaliser) already set, so a managed screen's saved choices survive. Returns the original
 * metadata untouched when nothing is missing (no needless clone / re-render).
 */
export const reconcileFormBoParams = (
  metadata: ICRUDFormDiMetadata,
  boParams: IWFParameter[],
  outputIds: Set<string>
): ICRUDFormDiMetadata => {
  const present: Set<string> = new Set(
    metadata.sections
      .flatMap((section: IFormSection): ICRUDFormField[] => section.items)
      .map((field: ICRUDFormField): string | undefined => field.linkedWorkflowBoParameterId)
      .filter((id: string | undefined): id is string => !!id)
  );
  const missing: IWFParameter[] = boParams.filter(
    (p: IWFParameter): boolean => p.isObject !== true && !present.has(p.id)
  );
  if (missing.length === 0) return metadata;
  // No section to hang the fields on — nothing we can safely do; leave the form as-is.
  if (metadata.sections.length === 0) return metadata;

  const next: ICRUDFormDiMetadata = structuredClone(metadata);
  const target: IFormSection = next.sections[0];
  for (const param of missing) {
    target.items.push(buildBoParamField(param, outputIds.has(param.id)));
  }
  return next;
};

/**
 * Guarantees every section has an id. The wire model (`CRUDFormSection`) carried NO id until
 * KOA-18273 — every server-derived or previously-stored section arrived with `id: undefined`,
 * which made it invisible to id-based targeting (drag-and-drop hit-testing, section editing,
 * React keys). Run over every metadata copy entering the tab; returns the same object (no clone,
 * no re-render) when nothing is missing.
 */
export const ensureFormSectionIds = (metadata: ICRUDFormDiMetadata): ICRUDFormDiMetadata => {
  const missing: boolean = metadata.sections.some((s: IFormSection): boolean => !s.id);
  if (!missing) return metadata;
  const next: ICRUDFormDiMetadata = structuredClone(metadata);
  for (const section of next.sections) {
    if (!section.id) section.id = Guid.create().toString();
  }
  return next;
};

/**
 * Sections live and die with their fields (the primary section is exempt): a NON-FIRST section
 * whose last field was just removed or dragged away disappears — an empty extra block on the
 * generated form is never useful, and the ghost drop target always offers a fresh one.
 */
const pruneEmptiedSection = (metadata: ICRUDFormDiMetadata, section: IFormSection): void => {
  if (section.items.length > 0) return;
  const index: number = metadata.sections.indexOf(section);
  if (index <= 0) return;
  metadata.sections = metadata.sections.filter((s: IFormSection): boolean => s !== section);
};

/**
 * Move a form field by drag-and-drop: to `targetIndex` within `targetSectionId` (reordering), or
 * appended to that section's end when `targetIndex` is omitted (dropping on the section body).
 * The index is an INSERTION slot in the target section's current item list — when the field moves
 * within its own section, the slot is re-based after removal so "drop below the next row" lands
 * where the indicator showed. A cross-section move that empties a non-first source section drops
 * that section. Returns the original metadata untouched (no clone, no re-render, no dirty mark)
 * when the move is a no-op: unknown field/section, or a same-section drop back onto the field's
 * own position.
 */
export const moveFormField = (
  metadata: ICRUDFormDiMetadata,
  fieldId: string,
  targetSectionId: string,
  targetIndex?: number
): ICRUDFormDiMetadata => {
  const from: IFormSection | undefined = metadata.sections.find((s: IFormSection): boolean =>
    s.items.some((f: ICRUDFormField): boolean => f.id === fieldId)
  );
  const to: IFormSection | undefined = metadata.sections.find((s: IFormSection): boolean => s.id === targetSectionId);
  if (!from || !to) return metadata;

  const fromIndex: number = from.items.findIndex((f: ICRUDFormField): boolean => f.id === fieldId);
  const requestedIndex: number = targetIndex ?? to.items.length;
  const clampedIndex: number = Math.max(0, Math.min(requestedIndex, to.items.length));
  const sameSection: boolean = from.id === to.id;
  // Removing the field first shifts everything after it up by one — re-base the insertion slot.
  let insertIndex: number = clampedIndex;
  if (sameSection && fromIndex < clampedIndex) insertIndex = clampedIndex - 1;
  if (sameSection && insertIndex === fromIndex) return metadata;

  const next: ICRUDFormDiMetadata = structuredClone(metadata);
  const nextFrom: IFormSection = next.sections.find((s: IFormSection): boolean => s.id === from.id) as IFormSection;
  const nextTo: IFormSection = next.sections.find((s: IFormSection): boolean => s.id === to.id) as IFormSection;
  const [moved] = nextFrom.items.splice(fromIndex, 1);
  nextTo.items.splice(insertIndex, 0, moved);
  if (!sameSection) pruneEmptiedSection(next, nextFrom);
  return next;
};

/**
 * Drop on the GHOST section: detach the dragged field into a brand-new trailing section (the
 * design's "drag a field here to create the next section"). The new section clones the last
 * section's responsive layout; a non-first source section emptied by the move is dropped. No-ops
 * (same object back): unknown field, no section to clone the layout from, or detaching the sole
 * field of the LAST extra section — that would just recreate the same shape one id over.
 */
export const moveFormFieldToNewSection = (metadata: ICRUDFormDiMetadata, fieldId: string): ICRUDFormDiMetadata => {
  const from: IFormSection | undefined = metadata.sections.find((s: IFormSection): boolean =>
    s.items.some((f: ICRUDFormField): boolean => f.id === fieldId)
  );
  const template: IFormSection | undefined = metadata.sections[metadata.sections.length - 1];
  if (!from || !template) return metadata;
  const fromIndex: number = metadata.sections.indexOf(from);
  const isSoleFieldOfLastExtraSection: boolean =
    from.items.length === 1 && fromIndex > 0 && fromIndex === metadata.sections.length - 1;
  if (isSoleFieldOfLastExtraSection) return metadata;

  const next: ICRUDFormDiMetadata = structuredClone(metadata);
  const nextFrom: IFormSection = next.sections[fromIndex];
  const itemIndex: number = nextFrom.items.findIndex((f: ICRUDFormField): boolean => f.id === fieldId);
  const [moved] = nextFrom.items.splice(itemIndex, 1);
  next.sections.push({
    id: Guid.create().toString(),
    title: null,
    items: [moved],
    responsiveLayoutSizes: structuredClone(template.responsiveLayoutSizes)
  });
  pruneEmptiedSection(next, nextFrom);
  return next;
};

/**
 * Delete a field (the row trash). A non-first section emptied by the removal is dropped with it.
 * Returns the original metadata when the field isn't in the given section.
 */
export const removeFormField = (
  metadata: ICRUDFormDiMetadata,
  sectionId: string,
  fieldId: string
): ICRUDFormDiMetadata => {
  const section: IFormSection | undefined = metadata.sections.find((s: IFormSection): boolean => s.id === sectionId);
  const exists: boolean = !!section?.items.some((f: ICRUDFormField): boolean => f.id === fieldId);
  if (!section || !exists) return metadata;
  const next: ICRUDFormDiMetadata = structuredClone(metadata);
  const nextSection: IFormSection = next.sections.find(
    (s: IFormSection): boolean => s.id === sectionId
  ) as IFormSection;
  nextSection.items = nextSection.items.filter((f: ICRUDFormField): boolean => f.id !== fieldId);
  pruneEmptiedSection(next, nextSection);
  return next;
};

/**
 * The subset of loaded step metadata that Apply Screens actually sends. Selecting a step in the
 * sidebar loads (and caches) its form metadata, so `metadataByStep` accumulates an entry for every
 * step the user merely LOOKED at — pushing all of them would regenerate screens the user never
 * touched (rewriting their trees, scrambling friendly names, and dirtying branching diffs). Only a
 * step the user edited (dirty) or explicitly chose to build a new screen for (draft) is applied.
 */
export const selectApplyEntries = (
  metadataByStep: readonly IFormMetadataByStep[],
  dirtyStepIds: ReadonlySet<string>,
  draftStepIds: ReadonlySet<string>
): IFormMetadataByStep[] =>
  metadataByStep.filter(
    (entry: IFormMetadataByStep): boolean => dirtyStepIds.has(entry.stepId) || draftStepIds.has(entry.stepId)
  );

/** A successful Apply result to adopt into the tab's local metadata. */
export interface IApplyStepAdoption {
  stepId: string;
  formMetadata: ICRUDFormDiMetadata;
}

/** A step whose Apply failed — either an explicit backend failure or a missing result. */
export interface IApplyStepFailure {
  stepId: string;
  message: string | null;
}

export interface IApplyResultPartition {
  adopted: IApplyStepAdoption[];
  failures: IApplyStepFailure[];
}

/**
 * Partition an Apply Screens response for the steps THIS tab actually sent. The stream endpoint
 * can return other processes' steps (legacy whole-project apply), so results are matched by our
 * process id (case-insensitively — Guid spelling differs between the client workflow id and the
 * serialized response) plus a sent step id. A slot can be `null`: the backend stream leaves the
 * response slot unset when a step throws outside the per-step catch (`step_error` event), so a
 * sent step with NO surviving result is also reported as a failure — never silently dropped.
 */
export const partitionApplyResults = (
  results: readonly (IFormMetadataByStepResponse | null | undefined)[],
  processId: string,
  sentStepIds: readonly string[]
): IApplyResultPartition => {
  const ourStepIds: Set<string> = new Set(sentStepIds);
  const adopted: IApplyStepAdoption[] = [];
  const failures: IApplyStepFailure[] = [];
  const answered: Set<string> = new Set();
  for (const result of results) {
    if (!result) continue;
    const sameProcess: boolean = String(result.processId).toLowerCase() === processId.toLowerCase();
    if (!sameProcess || !ourStepIds.has(result.stepId)) continue;
    answered.add(result.stepId);
    if (result.isSuccess && result.formMetadata) {
      adopted.push({ stepId: result.stepId, formMetadata: result.formMetadata });
    } else if (!result.isSuccess) {
      failures.push({ stepId: result.stepId, message: result.message });
    }
  }
  for (const stepId of ourStepIds) {
    if (!answered.has(stepId)) {
      failures.push({ stepId, message: "The server returned no result for this step." });
    }
  }
  return { adopted, failures };
};
